import * as XLSX from 'xlsx';

interface ColumnWidth {
  wch: number;
}

interface WidthOptions {
  padding?: number;
  min?: number;
  max?: number;
}

const DEFAULT_PADDING = 2;
const DEFAULT_MIN_WIDTH = 8;
const DEFAULT_MAX_WIDTH = 50;

/**
 * Calcula o comprimento de uma string considerando caracteres especiais
 * Caracteres largos (como números) contam como 1.2x, caracteres normais como 1x
 */
const getStringWidth = (str: string): number => {
  if (!str) return 0;
  
  let width = 0;
  for (let char of str) {
    // Caracteres largos (números, maiúsculas, etc) contam mais
    if (/[0-9A-Z@#$%&]/.test(char)) {
      width += 1.2;
    } else if (/[a-z]/.test(char)) {
      width += 0.8;
    } else {
      width += 1;
    }
  }
  
  return width;
};

/**
 * Calcula as larguras das colunas dinamicamente baseado nos títulos e dados
 * Para estrutura de objetos (json_to_sheet)
 * 
 * @param data - Array de objetos com os dados a serem exportados
 * @param options - Opções de largura (padding, min, max)
 * @returns Array de objetos com propriedade 'wch' (width characters) para cada coluna
 * 
 * @example
 * const columns = calculateColumnWidths(data);
 * ws['!cols'] = columns;
 */
export const calculateColumnWidths = (
  data: Record<string, any>[],
  options?: WidthOptions
): ColumnWidth[] => {
  if (!data || data.length === 0) {
    return [];
  }

  const padding = options?.padding ?? DEFAULT_PADDING;
  const min = options?.min ?? DEFAULT_MIN_WIDTH;
  const max = options?.max ?? DEFAULT_MAX_WIDTH;

  const headers = Object.keys(data[0]);

  const columnWidths = headers.map((header) => {
    let maxWidth = getStringWidth(header.toString());

    for (let row of data) {
      const cellValue = row[header];
      const cellWidth = getStringWidth(cellValue?.toString() || "");
      maxWidth = Math.max(maxWidth, cellWidth);
    }

    const finalWidth = Math.min(
      Math.max(maxWidth + padding, min),
      max
    );

    return { wch: finalWidth };
  });

  return columnWidths;
};

/**
 * Calcula as larguras das colunas dinamicamente para array de arrays (aoa_to_sheet)
 * 
 * @param rows - Array de arrays com os dados
 * @param options - Opções de largura (padding, min, max)
 * @returns Array de objetos com propriedade 'wch' para cada coluna
 * 
 * @example
 * const columns = calculateColumnWidthsFromArrays(rows);
 * ws['!cols'] = columns;
 */
export const calculateColumnWidthsFromArrays = (
  rows: any[][],
  options?: WidthOptions
): ColumnWidth[] => {
  if (!rows || rows.length === 0) {
    return [];
  }

  const padding = options?.padding ?? DEFAULT_PADDING;
  const min = options?.min ?? DEFAULT_MIN_WIDTH;
  const max = options?.max ?? DEFAULT_MAX_WIDTH;

  // Descobrir número de colunas
  const numCols = Math.max(...rows.map(row => row.length));
  const columnWidths: ColumnWidth[] = [];

  for (let colIndex = 0; colIndex < numCols; colIndex++) {
    let maxWidth = 0;

    for (let row of rows) {
      const cellValue = row[colIndex];
      const cellWidth = getStringWidth(cellValue?.toString() || "");
      maxWidth = Math.max(maxWidth, cellWidth);
    }

    const finalWidth = Math.min(
      Math.max(maxWidth + padding, min),
      max
    );

    columnWidths.push({ wch: finalWidth });
  }

  return columnWidths;
};

/**
 * Aplica as larguras calculadas a uma planilha do XLSX (para json_to_sheet)
 * 
 * @param worksheet - Planilha do XLSX
 * @param data - Array de objetos com os dados
 * @param options - Opções de largura (padding, min, max)
 * 
 * @example
 * const ws = XLSX.utils.json_to_sheet(data);
 * applyColumnWidths(ws, data);
 */
export const applyColumnWidths = (
  worksheet: XLSX.WorkSheet,
  data: Record<string, any>[],
  options?: WidthOptions
): void => {
  const columnWidths = calculateColumnWidths(data, options);
  worksheet['!cols'] = columnWidths;
};

/**
 * Aplica as larguras calculadas a uma planilha do XLSX (para aoa_to_sheet)
 * 
 * @param worksheet - Planilha do XLSX
 * @param rows - Array de arrays com os dados
 * @param options - Opções de largura (padding, min, max)
 * 
 * @example
 * const ws = XLSX.utils.aoa_to_sheet(rows);
 * applyColumnWidthsFromArrays(ws, rows);
 */
export const applyColumnWidthsFromArrays = (
  worksheet: XLSX.WorkSheet,
  rows: any[][],
  options?: WidthOptions
): void => {
  const columnWidths = calculateColumnWidthsFromArrays(rows, options);
  worksheet['!cols'] = columnWidths;
};
